/**
 * Confirmation dialog for skipping the current guideline without reviewing its
 * criteria.
 *
 * The reviewer picks one fixed reason (or "Others" plus a free-text reason).
 * Confirming records a "Not Met" human verdict on the guideline with the
 * chosen reason as its `verdict_reasoning`, then advances to the next
 * guideline.
 * @module medicalNecessityAnalysis/components/SkipToNextGuidelineModal
 */
import React, { useState } from "react";
import AlertCircleIcon from "../../../svg/alert-circle-icon";

/** Reason that triggers the "evidence added" reminder nudge. */
const NOT_MET_REASON = "The criteria was Not Met, invalidating the guideline";

/** The fixed skip reasons shown as radio options, in display order. */
const SKIP_REASONS = [
  "The histology did not match",
  NOT_MET_REASON,
  "The category was not the most appropriate one",
  "Others",
] as const;

/** The "Others" option, which additionally requires free-text detail. */
const OTHERS_REASON = "Others";

/** Non-blocking reminder shown under the reason picker for {@link NOT_MET_REASON}. */
const NOT_MET_NUDGE =
  "Please ensure your disagreement with the AI’s verdict is supported by relevant evidence.";

/** Props for {@link SkipToNextGuidelineModal}. */
interface SkipToNextGuidelineModalProps {
  /** Whether the skip is currently being persisted. */
  isSaving: boolean;
  /** Closes the dialog without recording a verdict. */
  onCancel: () => void;
  /** Confirms the skip with the selected reason text. */
  onSkip: (reasoning: string) => void;
}

/**
 * Presents the skip reasons and confirms the guideline skip.
 *
 * @param props - Saving flag plus cancel/confirm handlers
 * @returns Modal dialog with the reason picker and confirmation controls
 */
export const SkipToNextGuidelineModal: React.FC<
  SkipToNextGuidelineModalProps
> = ({ isSaving, onCancel, onSkip }) => {
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [othersText, setOthersText] = useState("");

  const isOthers = selectedReason === OTHERS_REASON;
  const canConfirm =
    !isSaving &&
    !!selectedReason &&
    (!isOthers || othersText.trim().length > 0);

  const handleConfirm = () => {
    if (!canConfirm || !selectedReason) return;
    const reasoning = isOthers
      ? `${OTHERS_REASON}: ${othersText.trim()}`
      : selectedReason;
    onSkip(reasoning);
  };

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-6"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isSaving) onCancel();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="skip-guideline-title"
        className="flex w-full max-w-[31rem] flex-col rounded-md bg-white shadow-xl"
      >
        <div className="flex h-14 items-center justify-between self-stretch border-b border-[#E6E6E6] px-4 py-[2px]">
          <h2
            id="skip-guideline-title"
            className="text-base font-bold leading-6 text-[#0F0F0F] [font-family:Lato]"
          >
            Skip to Next Guideline
          </h2>
          <button
            type="button"
            aria-label="Close skip guideline dialog"
            disabled={isSaving}
            onClick={onCancel}
            className="text-lg leading-none text-[#6B6B6B]"
          >
            ×
          </button>
        </div>

        <div className="flex flex-col items-start gap-5 self-stretch px-4 pb-[15px] pt-4">
          <p className="m-0 text-[12px] font-normal leading-[18px] tracking-[0.2px] text-[#1F1F1F] [font-family:Lato]">
            Why are you skipping to the next guideline?
          </p>
          <div className="flex flex-col items-start gap-2 self-stretch">
            <div className="flex flex-col items-start gap-1.5 self-stretch">
              {SKIP_REASONS.map((reason) => (
                <label
                  key={reason}
                  className="flex cursor-pointer items-center gap-2"
                >
                  <input
                    type="radio"
                    name="skip-guideline-reason"
                    value={reason}
                    checked={selectedReason === reason}
                    disabled={isSaving}
                    onChange={() => setSelectedReason(reason)}
                    className="h-4 w-4 accent-[#0056D6]"
                  />
                  <span className="text-[11px] font-normal leading-[11px] text-[#444] [font-family:Lato]">
                    {reason}
                  </span>
                </label>
              ))}
            </div>
            {selectedReason === NOT_MET_REASON && (
              <p
                role="status"
                className="m-0 flex items-start gap-1.5 p-0 text-[11px] font-normal leading-[15px] text-[#9B1C1C] [font-family:Lato]"
              >
                <span className="shrink-0 pt-[1px]">
                  <AlertCircleIcon width="14" height="14" stroke="#9B1C1C" />
                </span>
                {NOT_MET_NUDGE}
              </p>
            )}
          </div>
          {isOthers && (
            <input
              id="skip-guideline-others-reason"
              type="text"
              autoFocus
              placeholder="Enter Reason"
              value={othersText}
              disabled={isSaving}
              onChange={(event) => setOthersText(event.target.value)}
              className="h-9 w-full rounded border border-[#D6D6D6] bg-white px-3 text-[12px] font-normal leading-4 text-[#0F0F0F] outline-none placeholder:text-[#8C8C8C] focus:border-[#0F0F0F]"
            />
          )}
        </div>

        <div className="flex justify-end gap-3 border-t border-[#E6E6E6] px-4 py-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSaving}
            className="h-9 flex-1 rounded border border-[#0F0F0F] bg-white px-4 text-[11px] font-bold text-[#0F0F0F] disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm}
            className="h-9 flex-1 rounded bg-[#0F0F0F] px-5 text-[11px] font-bold text-white disabled:opacity-50"
          >
            {isSaving ? "Skipping..." : "Skip to Next"}
          </button>
        </div>
      </div>
    </div>
  );
};
